import PropTypes from 'prop-types';
import { basePageWrap } from '../BasePage';
import styles from './SoundCollectionPage.module.scss';
import Breadcrumbs from '../../components/Breadcrumbs';
import {
    setGetArguments,
    getSelectedProductIdFromCookie,
} from '../../utils/singleProductChooser';

// Components
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import HeroButtonModal from '../../components/HeroButtonModal/HeroButtonModal';
import AudioPlayerList from '../../components/AudioPlayerList';
import TabLinksVertical from '../../components/TabLinksVertical';
import DownloadProductChooserModal from '../../components/DownloadProductChooserModal';
import StreamField from '../../components/StreamField';
import RawHtml from '../../components/RawHtml';

const SoundCollectionPage = ({
    title,
    hero = {},
    breadcrumbs = {
        links: [],
    },
    settings = {
        header: {},
        footer: {},
    },
    soundDemos,
    compatibleProducts,
    downloadOptions,
    sampleInstruments,
    tabLinksData = { primary: [], secondary: [] },
    selectedProductId,
    soundContent,
    productSelectionOptions,
    richText,
    isFileOnlyDownload,
}) => {
    const cookieValue = getSelectedProductIdFromCookie();
    const hasSoundDemos =
        soundDemos && soundDemos.items && soundDemos.items.length > 0;

    const tabLinks = {
        ...tabLinksData,
        primary: tabLinksData.primary.map((x) => ({
            ...x,
            href: setGetArguments(x.href, cookieValue ? cookieValue : null),
        })),
    };

    const isSingleProduct =
        downloadOptions &&
        downloadOptions['products'] &&
        downloadOptions['products'].length == 1;
    if (isSingleProduct) {
        downloadOptions['downloadUrl'] =
            downloadOptions['products'][0]['downloadUrl'];
    }

    const handleDataLayerPushSignatureSoundBankPage = (product) => {
        if (!product || !product.title) {
            console.warn(
                'Missing or invalid product for dataLayer push',
                product
            );
            return;
        }

        const data = {
            event: 'sounds_download',
            collection_name: 'SoundCollectionPage',
            sound_name: title,
            product_name: product.title,
            version: null,
            sample_name: null,
        };

        if (typeof window.dataLayer !== 'undefined') {
            window.dataLayer.push(data);
            console.log('Data pushed to dataLayer:', data);
        } else {
            console.error('dataLayer is not defined');
        }
    };

    return (
        <div className={styles['SoundCollectionPage']}>
            <Header {...settings.header} />
            <main>
                <div className={styles['SoundCollectionPage__Container']}>
                    <div className={styles['SoundCollectionPage__Breadcrumbs']}>
                        <Breadcrumbs {...breadcrumbs} />
                    </div>
                    <div
                        className={
                            styles['SoundCollectionPage__ContentContainer']
                        }>
                        <div className={styles['SoundCollectionPage__Desktop']}>
                            {tabLinks && <TabLinksVertical {...tabLinks} />}
                        </div>
                        <div className={styles['SoundCollectionPage__Content']}>
                            {hero && (
                                <HeroButtonModal
                                    {...hero}
                                    modifiers={['alternative']}
                                    modal={{
                                        buttonText: `Download ${hero.title}`,
                                    }}>
                                    <DownloadProductChooserModal
                                        {...downloadOptions}
                                        title={hero.title}
                                        onDownload={
                                            handleDataLayerPushSignatureSoundBankPage
                                        }
                                        isFileOnlyDownload={isFileOnlyDownload}
                                    />
                                </HeroButtonModal>
                            )}

                            <div
                                className={
                                    styles['SoundCollectionPage__Mobile']
                                }>
                                {tabLinks && <TabLinksVertical {...tabLinks} />}
                            </div>

                            {richText && (
                                <div
                                    className={
                                        styles['SoundCollectionPage__RichText']
                                    }>
                                    <RawHtml html={richText} />
                                </div>
                            )}

                            {hasSoundDemos && (
                                <AudioPlayerList
                                    download={false}
                                    column={true}
                                    {...soundDemos}
                                    onDownload={
                                        handleDataLayerPushSignatureSoundBankPage
                                    }
                                />
                            )}

                            {sampleInstruments &&
                                sampleInstruments.length > 0 && (
                                    <AudioPlayerList
                                        selectedProductId={selectedProductId}
                                        productList={compatibleProducts}
                                        hideChooser={true}
                                        download={true}
                                        column={true}
                                        items={sampleInstruments}
                                        onDownload={
                                            handleDataLayerPushSignatureSoundBankPage
                                        }
                                    />
                                )}
                            {soundContent && soundContent.length > 0 && (
                                <StreamField items={soundContent} />
                            )}
                        </div>
                    </div>
                </div>
            </main>
            <Footer {...settings.footer} />
        </div>
    );
};

SoundCollectionPage.propTypes = {
    settings: PropTypes.object,
    hero: PropTypes.object,
    breadcrumbs: PropTypes.object,
    tabLinksData: PropTypes.shape({
        primary: PropTypes.array,
        secondary: PropTypes.array,
    }),
    sampleInstruments: PropTypes.object,
};

export default basePageWrap(SoundCollectionPage);
